/**
 * Spend → tier.
 *
 * Tiering is RECOMPUTED FROM LIVE SPEND on every call. Accounts are never
 * stored in a tier, which is what makes a threshold slider work — and also what
 * makes accounts move tier silently when spend refreshes. That movement is real
 * and expected; surfacing it to the user is the caller's job (the tier-change
 * report), not something to paper over here.
 */

import type { Account, TierDef, TierPins } from './types.ts';

/**
 * Is this tier filled by hand rather than by spend?
 *
 * One place, because the absent-means-'band' default has to be read the same way
 * everywhere — a second reader spelling it `tier.selection === 'band'` would
 * treat every pre-existing tier as named the moment the field is missing.
 */
export function isNamedTier(tier: TierDef): boolean {
  return tier.selection === 'named';
}

/** The tiers whose membership spend decides. Named tiers never appear here. */
export function bandTiers(tiers: readonly TierDef[]): TierDef[] {
  return tiers.filter((t) => !isNamedTier(t));
}

/**
 * Throws if tiers are not ordered highest-threshold-first, or if the lowest tier
 * does not admit every remaining value.
 *
 * Worth failing loudly on: tierOf() returns the FIRST tier whose threshold the
 * spend clears, so an out-of-order list does not error — it quietly puts
 * enterprise accounts in the small band, and every downstream number is wrong
 * while looking entirely plausible.
 */
export function assertTiersOrdered(tiers: readonly TierDef[]): void {
  if (tiers.length === 0) throw new Error('staffing: no tiers configured');

  // Two tiers sharing a key pass every other check and then corrupt the totals:
  // bucketByTier re-initializes `out[key]` so the first tier's accounts are
  // discarded, and computePortfolios iterates the key twice, emitting duplicate
  // portfolios whose load double-counts — while coverage stays green, because it
  // never sees the same account under two different CSMs. Same class as
  // assertSlotsUnique, same remedy.
  const seenKeys = new Set<string>();
  for (const tier of tiers) {
    if (seenKeys.has(tier.key)) {
      throw new Error(
        `staffing: duplicate tier key '${tier.key}' — each tier must have its own`,
      );
    }
    seenKeys.add(tier.key);
  }

  // A named tier must be UNREACHABLE by spend. The band walk skips them, and
  // this makes that skip belt-and-braces: even a caller who filtered wrongly
  // could not land an account in a hand-picked tier by accident.
  for (const tier of tiers) {
    if (isNamedTier(tier) && tier.minSpendM !== Number.POSITIVE_INFINITY) {
      throw new Error(
        `staffing: tier '${tier.key}' is hand-picked, so no spend may reach it — its minSpendM must be Number.POSITIVE_INFINITY, not ${tier.minSpendM}`,
      );
    }
  }

  // Order is checked among the BANDS only. Named tiers hold no threshold, so
  // they are not "out of order" wherever they sit — their position in the list
  // is display order and nothing else.
  const bands = bandTiers(tiers);
  if (bands.length === 0) {
    throw new Error(
      'staffing: every tier is hand-picked — with no band there is no catch-all, and any account nobody picked would fall out of the book entirely',
    );
  }

  for (let i = 1; i < bands.length; i += 1) {
    const prev = bands[i - 1]!;
    const cur = bands[i]!;
    if (cur.minSpendM >= prev.minSpendM) {
      throw new Error(
        `staffing: tiers must be ordered by descending minSpendM — '${cur.key}' (${cur.minSpendM}) is not below '${prev.key}' (${prev.minSpendM})`,
      );
    }
  }

  // The last tier must be a real catch-all. Everything downstream assumes the
  // tiers PARTITION the book: bucketByTier drops an account matching no tier,
  // and coverage counts only what landed somewhere — so a floor of 0 on the last
  // tier silently discards every credit/adjustment account with negative spend.
  // It reads as a rounding detail and behaves as data loss, which is why it is
  // asserted rather than documented. `assertInputsValid` already anticipates
  // -Infinity here; this makes the expectation binding.
  const last = bands[bands.length - 1]!;
  if (last.minSpendM !== Number.NEGATIVE_INFINITY) {
    throw new Error(
      `staffing: the last tier must catch everything — '${last.key}' has minSpendM ${last.minSpendM}, so accounts below it (negative spend from credits and adjustments) would be dropped from the book entirely; use Number.NEGATIVE_INFINITY`,
    );
  }
}

/**
 * The tier an account falls in.
 *
 * The lowest tier is the catch-all, which is how zero and NEGATIVE spend land
 * somewhere real rather than being dropped — 19 accounts in the reference
 * snapshot have spend <= 0, the lowest at -$0.001M.
 */
export function tierOf(spendM: number, tiers: readonly TierDef[]): string {
  const bands = bandTiers(tiers);
  for (const tier of bands) {
    if (spendM >= tier.minSpendM) return tier.key;
  }
  return bands[bands.length - 1]!.key;
}

/**
 * Bucket accounts by tier key. Every configured tier gets an entry, possibly empty.
 *
 * A HAND-PICKED PLACEMENT BEATS THE BAND. That order is the whole point: a
 * strategic account is a decision, and if spend could override the decision the
 * account would leave the tier the moment its bookings moved — which is exactly
 * the silent re-tiering this module recomputes-from-live-spend to expose, and
 * exactly what a manual placement exists to prevent.
 *
 * An assignment naming a tier that does not exist THROWS rather than falling
 * back to the band. Falling back would put a strategic account quietly into
 * Small and leave the numbers looking plausible; the glue layer drops and
 * REPORTS untranslatable assignments before they reach here, the same way it
 * already does for pins.
 */
export function bucketByTier(
  accounts: readonly Account[],
  tiers: readonly TierDef[],
  tierPins: TierPins = {},
): Record<string, Account[]> {
  assertTiersOrdered(tiers);
  const known = new Set(tiers.map((t) => t.key));
  for (const [accountId, tierKey] of Object.entries(tierPins)) {
    if (!known.has(tierKey)) {
      throw new Error(
        `staffing: account '${accountId}' is placed in tier '${tierKey}', which this configuration does not define — a placement that silently fell back to the spend band would move a hand-picked account without saying so`,
      );
    }
  }

  const out: Record<string, Account[]> = {};
  for (const tier of tiers) out[tier.key] = [];
  for (const account of accounts) {
    const placed = tierPins[account.id];
    out[placed ?? tierOf(account.spendM, tiers)]!.push(account);
  }
  return out;
}
